// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — onglet « Aides » d'une organisation : demandes paginées (filtre d'état), dépôt au guichet
// pour un salarié (jamais pour soi), synthèse par type SANS AUCUN NOM (vue agrégée ; sous le seuil d'anonymat, le
// serveur masque les chiffres d'un type). Une aide est une charge de l'organisation : jamais de créance.
// Routes : /api/dispositifs/aides (README du module).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ChevronRight, HeartHandshake, Plus } from 'lucide-react';
import { Bouton, Badge, BandeauInfo } from '../../design/composants';
import { toArray, aujourdhuiDakar } from './api';
import { Champ, Etat, PaginationDs } from './commun';
import { ChoixPersonne } from './FenetresMandat';
import { useSocial, francs, dateLongue, lireMontant, ETATS_AIDE } from './apiSocial';
import { useLectureSociale, EtatsLecture, FiltreEtats, FenetreActe, MarqueDemo, Montant, Tuiles } from './communSocial';
import { useProduits } from './PretsSection';
import { LigneSuivi } from '../../components/suivi/SuiviDemande'; // prochain acteur : `suivi` des lignes (lecture)

const FORMES = { FORFAIT: 'Forfait', BAREME: 'Barème', CAS_PAR_CAS: 'Cas par cas' };

function FenetreDepotAide({ produits, onFermer, onCree }) {
  const api = useSocial();
  const [produit, setProduit] = useState(produits.length === 1 ? produits[0].id : '');
  const types = useLectureSociale(() => (produit ? api.typesAide(produit, true) : Promise.resolve([])), [api, produit]);
  const [typeId, setTypeId] = useState('');
  const [personne, setPersonne] = useState(null);
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState('');
  const [motif, setMotif] = useState('');
  const liste = toArray(types.donnees);
  const type = liste.find((t) => t.id === typeId);
  const absentes = toArray(type?.reglement?.absentes);
  const m = montant.trim() ? lireMontant(montant) : null;
  return (
    <FenetreActe titre="Demande d'aide au guichet" Icone={HeartHandshake} libelleBouton="Enregistrer la demande"
      pret={!!(produit && type && personne && (!montant.trim() || m))} onFermer={onFermer}
      aide="Saisie pour un salarié qui se présente au guichet ; vous ne pouvez pas saisir votre propre demande. Les pièces s'ajoutent ensuite sur la fiche."
      onConfirmer={async (cle) => {
        const r = await api.deposerAide({
          dispositif_id: produit, type_aide_id: type.id, employe_id: personne.id,
          ...(m ? { montant_demande: m } : {}), ...(date ? { date_evenement: date } : {}), ...(motif.trim() ? { motif: motif.trim() } : {}),
        }, cle);
        onCree(r);
      }}>
      <Champ libelle="Dispositif d'aides" requis>
        <select className="rh-champ-saisie" value={produit} onChange={(e) => { setProduit(e.target.value); setTypeId(''); }}>
          <option value="">Choisir…</option>
          {produits.map((p) => <option key={p.id} value={p.id} disabled={p.etat !== 'ACTIF'}>{p.libelle}{p.etat !== 'ACTIF' ? ' (non actif)' : ''}</option>)}
        </select>
      </Champ>
      {produit && (
        <Champ libelle="Type d'aide" requis aide={types.erreur ? types.erreur.message : (!liste.length && types.donnees ? 'Aucun type d\'aide actif pour ce dispositif.' : undefined)}>
          <select className="rh-champ-saisie" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            <option value="">Choisir…</option>
            {liste.map((t) => <option key={t.id} value={t.id}>{t.libelle} — {FORMES[t.forme] || t.forme}{t.sensible ? ' (confidentiel)' : ''}</option>)}
          </select>
        </Champ>
      )}
      {type && (
        <p className="rh-form-aide" style={{ margin: '-4px 0 12px' }}>
          {type.reglement?.montant_forfait ? `Forfait : ${francs(type.reglement.montant_forfait)}. ` : ''}
          {type.reglement?.plafond_cas ? `Plafond par cas : ${francs(type.reglement.plafond_cas)}. ` : ''}
          {toArray(type.reglement?.pieces_exigees).length ? `Pièces exigées : ${toArray(type.reglement.pieces_exigees).join(', ').toLowerCase()}.` : 'Aucune pièce exigée.'}
          {absentes.length > 0 && ` Règles absentes : ${absentes.length} — le dépôt sera refusé tant que le règlement n'est pas complet.`}
        </p>
      )}
      <ChoixPersonne choisie={personne} onChoisir={setPersonne} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <Champ libelle="Montant demandé (F)" aide={type?.forme === 'CAS_PAR_CAS' ? 'Requis pour une aide au cas par cas.' : 'Facultatif.'}>
          <input className="rh-champ-saisie" inputMode="numeric" value={montant} onChange={(e) => setMontant(e.target.value)} />
        </Champ>
        <Champ libelle="Date de l'événement"><input type="date" className="rh-champ-saisie" max={aujourdhuiDakar()} value={date} onChange={(e) => setDate(e.target.value)} /></Champ>
      </div>
      <Champ libelle="Motif (confidentiel)" aide="Facultatif. Conservé à part : seules les personnes habilitées le lisent, et chaque lecture est tracée.">
        <textarea className="rh-champ-saisie" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </Champ>
    </FenetreActe>
  );
}

function exercicesProposes() {
  const a = Number(aujourdhuiDakar().slice(0, 4));
  return [a, a - 1, a - 2, a - 3];
}

function SyntheseAides({ organisationId }) {
  const api = useSocial();
  const [exercice, setExercice] = useState('');
  const s = useLectureSociale(() => api.syntheseAides(organisationId, exercice), [api, organisationId, exercice]);
  const d = s.donnees;
  const lignes = toArray(d?.lignes);
  return (
    <>
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <p className="rh-form-aide" style={{ margin: 0 }}>Vue agrégée : aucun nom n'y figure. Un type confidentiel est regroupé sous « Aide (type confidentiel) ».</p>
        <select className="rh-champ-saisie" aria-label="Exercice" value={exercice} onChange={(e) => setExercice(e.target.value)}>
          <option value="">Exercice en cours</option>
          {exercicesProposes().map((a) => <option key={a} value={a}>Exercice {a}</option>)}
        </select>
      </div>
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={s} Icone={BarChart3} estVide={!lignes.length} titreVide="Aucune aide sur cet exercice"
          vide="La synthèse se remplit au fil des décisions : nombre de demandes, d'aides accordées et versées, par type.">
          <div style={{ padding: '16px 16px 0' }}>
            <Tuiles tuiles={[
              { libelle: `Exercice ${d?.exercice || ''}`, valeur: d?.totaux?.beneficiaires_distincts ?? '—', aide: d?.totaux?.beneficiaires_distincts === null ? 'Masqué : sous le seuil d\'anonymat' : 'bénéficiaires distincts' },
              { libelle: 'Montant accordé', valeur: francs(d?.totaux?.montant_accorde) },
              { libelle: 'Montant versé', valeur: francs(d?.totaux?.montant_verse) },
            ]} />
          </div>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Type d'aide</th><th style={{ textAlign: 'right' }}>Demandes</th><th style={{ textAlign: 'right' }}>Accordées</th><th style={{ textAlign: 'right' }}>Refusées</th><th style={{ textAlign: 'right' }}>En cours</th><th style={{ textAlign: 'right' }}>Bénéficiaires</th><th style={{ textAlign: 'right' }}>Accordé</th><th style={{ textAlign: 'right' }}>Versé</th></tr></thead>
              <tbody>
                {lignes.map((l) => (
                  <tr key={`${l.dispositif_id}-${l.type_code || 'conf'}`}>
                    <td>{l.type_libelle} {l.confidentiel && <Badge ton="neutre">Confidentiel</Badge>} <MarqueDemo actif={l.donnees_demo} /></td>
                    {l.masque ? (
                      <td colSpan={7} className="rh-cellule-secondaire">Chiffres masqués : moins de {d.seuil_anonymat} bénéficiaires.</td>
                    ) : (
                      <>
                        {['nb_demandes', 'nb_accordees', 'nb_refusees', 'nb_en_cours', 'nb_beneficiaires'].map((k) => <td key={k} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l[k]}</td>)}
                        <td style={{ textAlign: 'right' }}><Montant n={l.montant_accorde} /></td>
                        <td style={{ textAlign: 'right' }}><Montant n={l.montant_verse} /></td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </EtatsLecture>
      </section>
    </>
  );
}

const FILTRES = ['DEPOSEE', 'PIECES_A_VERIFIER', 'EN_INSTRUCTION', 'EN_ATTENTE_BUDGET', 'ACCORDEE', 'VERSEE', 'REFUSEE', 'ANNULEE'];

function ListeAides({ organisation, droits, produits }) {
  const api = useSocial();
  const naviguer = useNavigate();
  const [statut, setStatut] = useState('');
  const [page, setPage] = useState(1);
  const [depot, setDepot] = useState(false);
  const liste = useLectureSociale(() => api.aides(organisation.id, { page, taille: 25, statut }), [api, organisation.id, page, statut]);
  const elements = toArray(liste.donnees);
  const ouvrir = (d) => naviguer(`/dispositifs/${organisation.id}/aides/${d.id}`);
  const peutDeposer = droits.a('ds_aide_saisir_guichet') && organisation.etat === 'ACTIVE';
  return (
    <>
      <div className="rh-outils" style={{ justifyContent: 'space-between' }}>
        <FiltreEtats table={ETATS_AIDE} codes={FILTRES} valeur={statut} onChange={(c) => { setStatut(c); setPage(1); }} />
        {peutDeposer && <Bouton variante="primaire" Icone={Plus} disabled={!produits.length} onClick={() => setDepot(true)}>Déposer au guichet</Bouton>}
      </div>
      <section className="rh-carte" style={{ padding: 0 }}>
        <EtatsLecture lecture={liste} Icone={HeartHandshake} estVide={!elements.length}
          titreVide={statut ? 'Aucune demande dans cet état' : 'Aucune demande d\'aide'}
          vide={statut ? 'Choisissez un autre état.' : produits.length
            ? 'Les demandes d\'aide déposées au guichet apparaissent ici, de la vérification des pièces jusqu\'au versement.'
            : 'Aucun dispositif d\'aides n\'est encore préparé dans cette organisation : créez-le dans l\'onglet Dispositifs.'}>
          <div className="rh-conteneur-tableau">
            <table className="rh-tableau">
              <thead><tr><th>Demande</th><th>Type</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th><th aria-label="Ouvrir" /></tr></thead>
              <tbody>
                {elements.map((d) => (
                  <tr key={d.id} className="rh-ligne" tabIndex={0} onClick={() => ouvrir(d)} onKeyDown={(e) => { if (e.key === 'Enter') ouvrir(d); }}>
                    <td>
                      <span className="rh-cellule-principale">{d.salarie?.nom}</span>
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{d.numero} · {d.salarie?.matricule}{d.depose_le ? ` · déposée le ${dateLongue(d.depose_le)}` : ''} <MarqueDemo actif={d.donnees_demo} /></span>
                    </td>
                    <td>{d.type?.libelle}{d.type?.confidentiel && !d.type?.code ? '' : d.type?.confidentiel ? <Badge ton="neutre">Confidentiel</Badge> : null}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Montant n={d.montant_accorde ?? d.montant_demande} />
                      <span className="rh-cellule-secondaire" style={{ display: 'block' }}>{d.montant_accorde !== null && d.montant_accorde !== undefined ? 'accordé' : d.montant_demande !== null ? 'demandé' : ''}</span>
                    </td>
                    <td><Etat table={ETATS_AIDE} code={d.statut} /><LigneSuivi suivi={d.suivi} /></td>
                    <td style={{ width: 32, color: 'var(--sg-texte-4)' }}><ChevronRight size={16} aria-hidden="true" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationDs donnees={liste.donnees} onPage={setPage} />
        </EtatsLecture>
      </section>
      {depot && <FenetreDepotAide produits={produits} onFermer={() => setDepot(false)} onCree={(r) => { setDepot(false); ouvrir(r); }} />}
    </>
  );
}

export default function AidesSection({ organisation, droits }) {
  const [vue, setVue] = useState('demandes');
  const { produits } = useProduits(organisation.id, 'AIDE');
  return (
    <>
      <div className="rh-vues rh-vues-ligne" role="group" aria-label="Vue des aides">
        <button type="button" className="rh-vue" aria-pressed={vue === 'demandes'} onClick={() => setVue('demandes')}>Demandes</button>
        <button type="button" className="rh-vue" aria-pressed={vue === 'synthese'} onClick={() => setVue('synthese')}>Synthèse par type</button>
      </div>
      <BandeauInfo ton="info">Une aide est une charge de l'organisation, jamais une dette : ni créance, ni échéancier, ni retenue sur salaire.</BandeauInfo>
      {vue === 'demandes' ? <ListeAides organisation={organisation} droits={droits} produits={produits} /> : <SyntheseAides organisationId={organisation.id} />}
    </>
  );
}
